import { Component, DestroyRef, effect, EventEmitter, inject, Injector, Input, OnInit, Output, runInInjectionContext, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatOption, ThemePalette } from '@angular/material/core';
import { FloatLabelType, MatError, MatFormField, MatFormFieldAppearance, MatLabel } from '@angular/material/form-field';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { MatTooltip, TooltipPosition } from '@angular/material/tooltip';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';

// New-look replacement for app-filter-field-search-select. Matches on idField; select-all includes options
// hidden by search.
@Component({
  selector: 'app-filter-search-select',
  templateUrl: './filter-search-select.component.html',
  styleUrls: ['./filter-search-select.component.scss'],
  imports: [MatFormField, MatLabel, MatError, MatSelect, MatSelectTrigger, MatOption, MatTooltip, ReactiveFormsModule, NgxMatSelectSearchModule]
})
export class FilterSearchSelectComponent<T = any> implements OnInit {

  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _hidden = signal(false);
  private readonly _disabled = signal(false);

  @Input() set hidden(value: boolean) {
    this._hidden.set(value);
  }

  get hidden(): boolean {
    return this._hidden();
  }

  @Input() set disabled(value: boolean) {
    this._disabled.set(value);
  }

  get disabled(): boolean {
    return this._disabled();
  }

  @Input() compareWith: (a: any, b: any) => boolean = (a, b) => a === b || (a != null && b != null && a[this.idField] === b[this.idField]);
  @Input() form: FormGroup | null = null;
  @Input() controlName = 'field-search-select';
  @Input() color: ThemePalette = 'primary';
  @Input() appearance: MatFormFieldAppearance = 'outline';
  @Input() floatLabel: FloatLabelType = 'always';
  @Input() multiple = false;
  @Input() label = '';
  @Input() showLabel = true;
  @Input() placeholder = 'Make a Selection';
  @Input() toolTipPosition: TooltipPosition = 'above';
  @Input() required = false;
  @Input() noneEnabled = true;
  @Input() idField = 'ID';
  @Input() labelField = 'Description';
  @Input() hideSubscriptWrapper = false;
  @Input() noEntriesFoundLabel = 'No Payers Found';

  // Declared for parity; never emitted.
  @Output() ready = new EventEmitter<void>();
  @Output() valueChanged = new EventEmitter<any>();

  // Set by the parent.
  private _options: T[] = [];
  set options(value: T[]) {
    if (this._options === value) {
      return;
    }

    this._options = value ?? [];
    this.applySearch();
  }

  get options(): T[] {
    return this._options;
  }

  filteredOptions: T[] = [];
  readonly control = new FormControl<any>(null);
  readonly filterControl = new FormControl<string>('', { nonNullable: true });
  noneOption: any = {};

  ngOnInit(): void {
    this.noneOption = { [this.idField]: -1 };

    this.form?.addControl(this.controlName, this.control);

    if (this.required) {
      this.control.setValidators([Validators.required]);
    }

    runInInjectionContext(this.injector, () => {
      effect(() => {
        if (this._disabled()) {
          this.control.disable({ emitEvent: false });
        } else {
          this.control.enable({ emitEvent: false });
        }
      });
    });

    this.filterControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.applySearch());
  }

  // null clears, [] checks every option, otherwise adds the options with those IDs.
  setSelectedValues(values?: T[] | null) {
    if (!values) {
      this.control.reset();
      this.control.setValue([]);
      this.filterControl.setValue('');
      return;
    }

    if (values.length == 0) {
      this.allOptionClicked(true);
      return;
    }

    const ids = new Set(values.map(v => v[this.idField]));
    const current: T[] = this.control.value ?? [];
    const added = this._options.filter(o => ids.has(o[this.idField]) && !current.some(c => this.compareWith(c, o)));
    this.control.setValue([...current, ...added]);
  }

  setSingleValueByID(id: any) {
    const item = this._options.find(o => o[this.idField] === id);
    this.control.setValue(item ?? null);
  }

  setToNone() {
    this.control.setValue(this.noneOption);
  }

  setFirstOption() {
    this.control.setValue(this._options.length > 0 ? this._options[0] : null);
  }

  // [{[idField]: 0}] = every option.
  getValues(): any {
    if (this.allToggled()) {
      return [{ [this.idField]: 0 }];
    }

    const value = this.control.value;

    if (!value) {
      return this.multiple ? [] : null;
    }

    return this.multiple ? [...value] : [value];
  }

  // The select-all checkbox: a user change, so the control is marked dirty.
  onToggleAll(isChecked: boolean) {
    this.allOptionClicked(isChecked);
    this.control.markAsDirty();
  }

  allOptionClicked(isChecked: boolean) {
    if (!this.multiple) {
      return;
    }

    this.control.setValue(isChecked ? [...this._options] : []);
  }

  allToggled(): boolean {
    return this.multiple && this._options.length > 0 && this.selectedOptions().length === this._options.length;
  }

  indeterminate(): boolean {
    const count = this.selectedOptions().length;
    return this.multiple && count > 0 && count < this._options.length;
  }

  summary(): string {
    const selected = this.selectedOptions();

    if (selected.length <= 1) {
      return selected[0]?.[this.labelField] ?? '';
    }

    return this.allToggled() ? 'All' : `${selected[0][this.labelField]} +${selected.length - 1}`;
  }

  tooltip(): string {
    const selected = this.selectedOptions();
    return selected.length > 1 ? selected.map(o => o[this.labelField]).join(', ') : '';
  }

  onSelectionChange() {
    this.valueChanged.emit(this.control.value);
  }

  private selectedOptions(): T[] {
    const value = this.control.value;

    if (!this.multiple || !Array.isArray(value)) {
      return [];
    }

    return this._options.filter(o => value.some(v => this.compareWith(v, o)));
  }

  private applySearch() {
    const search = this.filterControl.value.trim().toLowerCase();
    this.filteredOptions = search
      ? this._options.filter(o => `${o[this.labelField] ?? ''}`.toLowerCase().includes(search))
      : this._options.slice();
  }
}
